<template>
  <component :is="component" v-bind="componentProps" @click="onClick">
    <slot :is-external="isExternal" />
  </component>
</template>

<script lang="ts" setup>
import { langcodes } from '#nuxt-language-negotiation/config'
import type { Langcode } from '#nuxt-language-negotiation/config'
const EXTERNAL_URL_EXTENSIONS = ['jpg', 'png', 'svg', 'pdf']

const language = useCurrentLanguage()
const { $texts } = useNuxtApp()

function getUrlExtension(url: string): string {
  try {
    const parts = url.split(/[#?]/)
    return parts[0]?.split('.').pop()?.trim() || ''
  } catch {
    return ''
  }
}

const props = defineProps<{
  to: string
  target?: string
  isLink?: boolean
  activeClass?: string
}>()

const router = useRouter()
const route = useRoute()

const href = computed(() => {
  if (!props.to) {
    return ''
  }

  if (props.to.startsWith('www')) {
    return 'https://' + props.to
  }

  return props.to
})

const component = computed(() => {
  return href.value ? 'a' : 'div'
})

const urlExtension = computed(() => {
  return getUrlExtension(href.value).toLowerCase()
})

const isExternal = computed(
  () =>
    href.value.startsWith('http') ||
    EXTERNAL_URL_EXTENSIONS.includes(urlExtension.value),
)

// If the target page of the URL is rendered by Drupal.
const isDrupalRouted = computed(
  () => href.value.includes('/user') || href.value.includes('/admin'),
)

const linkLanguage = computed(() => {
  if (isExternal.value) {
    return
  }
  const lang = href.value.substring(1, 3) as Langcode
  if (langcodes.includes(lang)) {
    return lang
  }
  return 'en'
})

const componentProps = computed<Record<string, string | string[]>>(() => {
  const componentProps: Record<string, any> = {
    class: [],
  }
  if (href.value) {
    componentProps.href = href.value

    if (isExternal.value) {
      componentProps.target = '_blank'
    }
  }

  if (props.isLink) {
    componentProps.class.push('link')
    componentProps.class.push(isExternal.value ? 'is-external' : 'is-internal')
  }

  if (isActive.value) {
    componentProps.class.push(props.activeClass || 'nuxt-link-exact-active')
  }
  if (!isExternal.value && linkLanguage.value !== language.value) {
    componentProps.class.push('is-untranslated')
    componentProps.title = $texts('untranslatedLink')
  }

  return componentProps
})
const isActive = computed(() => {
  return route.path && href.value ? route.path.startsWith(href.value) : false
})
function onClick(e: Event) {
  if (isExternal.value || isDrupalRouted.value) {
    return
  }

  e.preventDefault()
  router.push(href.value)
}
</script>

<style lang="postcss">
.icon-link {
  @apply inline-flex items-center gap-10;
  svg {
    @apply w-[1.25em] h-[1.25em];
  }
}
</style>
